import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Form, Input, message, Modal, notification, Select, Space, Spin, Table } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import { DocsUtil } from 'utils/docs-util';
import ManageopenapikeyIcon from 'svg/v2/newcs_dashboard_personalinfo_manageopenapikey_icon.svg';
import moment from 'moment';
import { getCrashSightMcpUrl } from 'components/user/CrashClawConfig/useQuickAddCrashSightMcp';
import style from './style.scss';

/** 将毫秒时间戳格式化为 yyyy-MM-dd HH:mm:ss */
function formatTime(millis) {
  if (!millis) {
    return '';
  }
  return moment(millis).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * 拼装可直接粘贴到 MCP 客户端的 mcp.json。
 *
 * 格式对齐官方帮助文档 mcpGuide/configuration（客户端侧是 type: "http"），
 * 不要与 CrashClaw 后台 MCP Server 的 serverConfig（transportType / timeout）混淆 —— 两者消费者不同。
 * url 由 getCrashSightMcpUrl() 按当前环境给出，Key 直接内嵌，避免用户手拼导致环境错配或漏 csk_ 前缀。
 */
function buildMcpConfigJson(apiKey) {
  return JSON.stringify({
    mcpServers: {
      crashsight: {
        type: 'http',
        url: getCrashSightMcpUrl(),
        headers: {
          'X-CSMCP-API-KEY': apiKey,
        },
      },
    },
  }, null, 2);
}

/** 令牌过期时间下拉选项，value 为对应天数 */
const EXPIRE_OPTIONS = [
  { value: 7, label: () => ze('7天', '7 Days') },
  { value: 30, label: () => ze('30天', '30 Days') },
  { value: 90, label: () => ze('90天', '90 Days') },
  { value: 365, label: () => ze('1年', '1 Year') },
];

/** 续期时长下拉选项，受后端单次续期上限（365天）约束 */
const RENEW_EXPIRE_OPTIONS = [
  { value: 7, label: () => ze('7天', '7 Days') },
  { value: 30, label: () => ze('30天', '30 Days') },
  { value: 90, label: () => ze('90天', '90 Days') },
  { value: 365, label: () => ze('1年', '1 Year') },
];

/**
 * MCP电子令牌管理组件
 *
 * 功能包括：
 * - 查看用户名下的MCP令牌列表
 * - 生成新的MCP令牌（两阶段Modal：填写表单 → 展示创建结果）
 * - 查看令牌详情
 * - 废弃（删除）令牌，操作前需二次确认
 */
const McpTokenManagement = () => {
  const [tokenList, setTokenList] = useState([]);
  const [loading, setLoading] = useState(false);

  // 生成令牌Modal相关状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createModalStage, setCreateModalStage] = useState('form'); // 'form': 表单阶段, 'success': 创建成功展示阶段
  const [createdToken, setCreatedToken] = useState(null);
  const [creating, setCreating] = useState(false);

  // 查看令牌详情Modal相关状态
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingToken, setViewingToken] = useState(null);

  // 续期Modal相关状态
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [renewingToken, setRenewingToken] = useState(null);
  const [renewing, setRenewing] = useState(false);

  const [form] = Form.useForm();
  const [renewForm] = Form.useForm();

  useEffect(() => {
    fetchTokenList();
  }, []);

  /** 调用 /api/mcp/listTokens 获取用户令牌列表 */
  async function fetchTokenList() {
    setLoading(true);
    try {
      const { json } = await RestHelper.post('/api/mcp/listTokens');
      setTokenList(json.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  /** 调用 /api/mcp/createToken 创建令牌，成功后切换Modal为结果展示阶段并刷新列表 */
  async function handleCreateToken() {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const { json } = await RestHelper.post('/api/mcp/createToken', {
        name: values.name,
        expireDays: values.expireDays,
      });
      setCreatedToken(json.data);
      setCreateModalStage('success');
      fetchTokenList();
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  }

  /** 废弃令牌，弹出二次确认后调用 /api/mcp/revokeToken，成功后刷新列表 */
  function handleRevokeToken(record) {
    Modal.confirm({
      title: ze('确认废弃', 'Confirm Revocation'),
      icon: <ExclamationCircleOutlined />,
      content: ze(
        `确认要废弃令牌"${record.name}"吗？废弃后该令牌将无法使用。`,
        `Are you sure you want to revoke token "${record.name}"? It will no longer be usable after revocation.`,
      ),
      okText: ze('确认', 'Confirm'),
      cancelText: ze('取消', 'Cancel'),
      onOk: async () => {
        await RestHelper.post('/api/mcp/revokeToken', { id: record.id });
        message.success(ze('令牌已废弃', 'Token revoked'));
        fetchTokenList();
      },
    });
  }

  /** 调用 /api/mcp/renewToken 续期令牌，成功后关闭Modal并刷新列表 */
  async function handleRenewToken() {
    try {
      const values = await renewForm.validateFields();
      setRenewing(true);
      await RestHelper.post('/api/mcp/renewToken', {
        id: renewingToken.id,
        expireDays: values.expireDays,
      });
      message.success(ze('令牌续期成功', 'Token renewed successfully'));
      setRenewModalVisible(false);
      fetchTokenList();
    } catch (e) {
      console.error(e);
      const msg = e?.json?.message;
      if (msg) {
        message.error(ze(msg, msg));
      }
    } finally {
      setRenewing(false);
    }
  }

  /** 计算令牌剩余可续期次数 */
  function getRemainingRenewCount(token) {
    if (!token) return 0;
    const used = token.renewCount || 0;
    const max = token.maxRenewCount || 0;
    return Math.max(0, max - used);
  }

  /** 判断令牌是否已经达到续期上限（次数） */
  function isRenewalExhausted(token) {
    if (!token) return false;
    if (getRemainingRenewCount(token) <= 0) return true;
    return false;
  }

  /** 令牌脱敏显示：****加上后6位，用于表格列展示 */
  function maskToken(apiKey) {
    if (!apiKey) return '';
    const suffix = apiKey.length > 6 ? apiKey.slice(-6) : apiKey;
    return `****${suffix}`;
  }

  /** 复制文本到剪贴板，成功/失败均有提示 */
  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      message.success(ze('已复制至剪贴板', 'Copied to clipboard'));
    } catch (e) {
      notification.error({ message: ze('复制失败', 'Failed to copy text to clipboard.') });
    }
  }

  /** 打开生成令牌Modal，重置表单和状态 */
  function openCreateModal() {
    form.resetFields();
    setCreateModalStage('form');
    setCreatedToken(null);
    setCreateModalVisible(true);
  }

  /** 关闭生成令牌Modal，重置状态 */
  function closeCreateModal() {
    setCreateModalVisible(false);
    setCreateModalStage('form');
    setCreatedToken(null);
  }

  /**
   * 渲染令牌详情内容，复用于"创建成功"和"查看详情"两个Modal
   * @param {object} token - 令牌对象（McpTokenVo）
   * @param {boolean} showCreatedAt - 是否显示创建时间（查看详情时为true）
   */
  function renderTokenDetail(token, showCreatedAt = false) {
    if (!token) return null;
    // 没有 Key 时不展示配置块 —— 空 Key 的配置用户复制过去必然 401，比不展示更糟
    const mcpConfigJson = token.apiKey ? buildMcpConfigJson(token.apiKey) : '';
    return (
      <div className={style.tokenDetail}>
        <Alert
          type="warning"
          message={ze(
            '请妥善保管您的个人令牌，注意个人信息安全，切勿泄露给他人。',
            'Please keep your personal token safe. Pay attention to information security and do not disclose it to others.',
          )}
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div className={style.detailRow}>
          <span className={style.detailLabel}>{ze('名称', 'Name')}</span>
          <span className={style.detailValue}>{token.name}</span>
        </div>
        {showCreatedAt && (
          <div className={style.detailRow}>
            <span className={style.detailLabel}>{ze('创建时间', 'Created At')}</span>
            <span className={style.detailValue}>{formatTime(token.createdAtMillis)}</span>
          </div>
        )}
        <div className={style.detailRow}>
            <span className={style.detailLabel}>{ze('过期时间', 'Expires At')}</span>
          <span className={style.detailValue}>{formatTime(token.expiresAtMillis)}</span>
        </div>
        <div className={style.detailRow}>
          <span className={style.detailLabel}>{ze('个人令牌', 'Token')}</span>
          <span
            className={style.tokenClickable}
            onClick={() => copyToClipboard(token.apiKey)}
          >{token.apiKey}</span>
        </div>
        {/* MCP 客户端配置：url 已按当前环境填充、Key 已内嵌，用户复制即可使用 */}
        {mcpConfigJson && (
          <div className={style.configSection}>
            <div className={style.configSectionTitle}>
              <span>
                {ze(
                  'MCP 客户端配置（已填入当前环境地址与本令牌，复制即可使用）',
                  'MCP client config (prefilled with the current environment URL and this token, ready to copy)',
                )}
              </span>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => copyToClipboard(mcpConfigJson)}
              >
                {ze('复制配置', 'Copy Config')}
              </Button>
            </div>
            <pre className={style.configSnippet}>{mcpConfigJson}</pre>
          </div>
        )}
      </div>
    );
  }

  /** 令牌表格列定义 */
  const columns = [
    {
      title: ze('名称', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: ze('令牌', 'Token'),
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (apiKey) => (
        <span
          className={style.tokenClickable}
          onClick={() => copyToClipboard(apiKey)}
        >{maskToken(apiKey)}</span>
      ),
    },
    {
      title: ze('过期时间', 'Expires At'),
      dataIndex: 'expiresAtMillis',
      key: 'expiresAtMillis',
      width: 180,
      render: (millis) => formatTime(millis),
    },
    {
      title: ze('操作', 'Actions'),
      key: 'actions',
      width: 240,
      render: (_, record) => {
        const renewDisabled = isRenewalExhausted(record);
        return (
          <Space size={20}>
            <Button type="link" style={{ padding: 0 }} onClick={() => { setViewingToken(record); setViewModalVisible(true); }}>
              {ze('查看', 'View')}
            </Button>
            <Button
              type="link"
              style={{ padding: 0 }}
              disabled={renewDisabled}
              onClick={() => {
                setRenewingToken(record);
                renewForm.resetFields();
                setRenewModalVisible(true);
              }}
            >
              {ze('续期', 'Renew')}
            </Button>
            <Button type="link" danger style={{ padding: 0 }} onClick={() => handleRevokeToken(record)}>
              {ze('废弃', 'Revoke')}
            </Button>
          </Space>
        );
      },
    },
  ];

  // 生成令牌Modal的footer：表单阶段使用默认按钮，成功阶段仅显示"复制个人令牌"
  const createModalFooter = createModalStage === 'form'
    ? undefined
    : [
      <Button
        key="copy"
        type="primary"
        onClick={() => {
          copyToClipboard(createdToken?.apiKey);
          closeCreateModal();
        }}
      >{ze('复制个人令牌', 'Copy Personal Token')}</Button>,
    ];

  return (
    <div>
      <Spin spinning={loading}>
        {/* 标题栏 */}
        <div className={style.mcpTop}>
          <ManageopenapikeyIcon width="14" height="14" viewBox="0 0 14 14" />
          <div>{ze('MCP电子令牌管理', 'MCP Token Management')}</div>
        </div>
        {/* 说明文字 */}
        <div className={style.mcpTitle}>
          {ze(
            '用户使用MCP时，需要用到令牌进行用户授权。',
            'When using MCP, a token is required for user authorization.',
          )}
          <a
            className={style.docsLink}
            href={DocsUtil.makeDocsUrl('/mcpGuide/overview')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ze('查看 MCP 使用指南', 'View MCP Guide')}
          </a>
        </div>
        {/* 正文区域：无令牌时显示Empty，有令牌时显示Table */}
        <div className={style.mcpBody}>
          {tokenList.length === 0 ? (
            <Empty description={ze('你还没有生成过MCP电子令牌', 'You have not generated an MCP token yet')}>
              <Button type="primary" onClick={openCreateModal}>
                {ze('生成令牌', 'Generate Token')}
              </Button>
            </Empty>
          ) : (
            <div>
              <Table
                columns={columns}
                dataSource={tokenList}
                rowKey="id"
                pagination={false}
              />
              <Button
                type="primary"
                onClick={openCreateModal}
                style={{ marginTop: 16 }}
              >{ze('生成令牌', 'Generate Token')}</Button>
            </div>
          )}
        </div>

        {/* 生成令牌Modal：表单阶段填写名称和过期时间，成功阶段展示令牌详情 */}
        <Modal
          title={createModalStage === 'form'
            ? ze('生成令牌', 'Generate Token')
            : ze('令牌创建成功', 'Token Created Successfully')}
          open={createModalVisible}
          onOk={createModalStage === 'form' ? handleCreateToken : undefined}
          onCancel={closeCreateModal}
          footer={createModalFooter}
          confirmLoading={creating}
          width={640}
        >
          {createModalStage === 'form' ? (
            <Form form={form} layout="vertical">
              <Form.Item
                name="name"
                label={ze('令牌名称', 'Token Name')}
                rules={[{ required: true, message: ze('请输入令牌名称', 'Please enter token name') }]}
              >
                <Input placeholder={ze('请输入令牌名称', 'Please enter token name')} />
              </Form.Item>
              <Form.Item
                name="expireDays"
                label={ze('过期时间', 'Expiration')}
                rules={[{ required: true, message: ze('请选择过期时间', 'Please select expiration') }]}
              >
                <Select placeholder={ze('请选择过期时间', 'Please select expiration')}>
                  {EXPIRE_OPTIONS.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>{opt.label()}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          ) : (
            renderTokenDetail(createdToken)
          )}
        </Modal>

        {/* 续期令牌Modal */}
        <Modal
          title={ze('续期令牌', 'Renew Token')}
          open={renewModalVisible}
          onOk={handleRenewToken}
          onCancel={() => setRenewModalVisible(false)}
          confirmLoading={renewing}
          width={640}
        >
          {renewingToken && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={ze(
                `本令牌最多可续期 ${renewingToken.maxRenewCount || 0} 次，已续期 ${renewingToken.renewCount || 0} 次，剩余 ${getRemainingRenewCount(renewingToken)} 次；单次续期最长 1 年。`,
                `This token can be renewed up to ${renewingToken.maxRenewCount || 0} times. Used ${renewingToken.renewCount || 0}, remaining ${getRemainingRenewCount(renewingToken)}. Each renewal extends up to 1 year.`,
              )}
            />
          )}
          <Form form={renewForm} layout="vertical">
            <Form.Item
              name="expireDays"
              label={ze('续期时长', 'Renewal Duration')}
              rules={[{ required: true, message: ze('请选择续期时长', 'Please select renewal duration') }]}
            >
              <Select placeholder={ze('请选择续期时长', 'Please select renewal duration')}>
                {RENEW_EXPIRE_OPTIONS.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>{opt.label()}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* 查看令牌详情Modal */}
        <Modal
          title={ze('查看令牌详情', 'Token Details')}
          open={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          width={640}
          footer={[
            <Button
              key="copy"
              type="primary"
              onClick={() => {
                copyToClipboard(viewingToken?.apiKey);
                setViewModalVisible(false);
              }}
            >{ze('复制个人令牌', 'Copy Personal Token')}</Button>,
          ]}
        >
          {renderTokenDetail(viewingToken, true)}
        </Modal>
      </Spin>
    </div>
  );
};

export default McpTokenManagement;
